/**
 * PackedPath: movement on the wire (docs/43 §2.1).
 *
 * A path is a string of printable ASCII chars (codes 32..127), one per
 * straight run:  c = 32 + dir * 12 + (len - 1),  dir ∈ 0..7, len ∈ 1..12.
 * Runs longer than 12 tiles are split. "" means idle.
 *
 * Coordinates: everything here is in RAW tile coordinates. A mover resting on
 * tile (3, 4) is at exactly {x: 3, y: 4}. The engine draws tile centres, so it
 * adds 0.5 to both axes (see `PathMover`).
 *
 * Dir order: `DIR_DX`/`DIR_DY` below are the wire order from doc 43. They are
 * NOT the engine A*'s neighbour order (orthogonals first). Convert engine
 * paths by tile delta with `dirFromDelta` / `packPathFromTiles`, never by
 * neighbour index.
 */
import { Gait } from './types';
export const DIR_DX = [1, 1, 0, -1, -1, -1, 0, 1];
export const DIR_DY = [0, 1, 1, 1, 0, -1, -1, -1];
/** Tile-space compass names, for logs only (E = +x, S = +y). */
export const DIR_NAMES = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];
export const isDiagonal = (d) => (d & 1) === 1;
export const MAX_PATH_TILES = 64;
export const MAX_PATH_RUNS = 32;
export const MAX_RUN_LEN = 12;
export const PATH_CHAR_MIN = 32;
export const PATH_CHAR_MAX = 32 + 7 * MAX_RUN_LEN + (MAX_RUN_LEN - 1); // 127
/** Direction of a single 8-neighbour step. Throws if (dx, dy) is not a unit step. */
export function dirFromDelta(dx, dy) {
    for (let d = 0; d < 8; d++) {
        if (DIR_DX[d] === dx && DIR_DY[d] === dy)
            return d;
    }
    throw new RangeError(`not a unit step: (${dx}, ${dy})`);
}
function encodeRun(dir, len) {
    return String.fromCharCode(PATH_CHAR_MIN + dir * MAX_RUN_LEN + (len - 1));
}
/** Packs per-tile directions into runs. No length limit here; see `isValidPackedPath`. */
export function packPath(dirs) {
    let out = '';
    let i = 0;
    while (i < dirs.length) {
        const d = dirs[i];
        if (!Number.isInteger(d) || d < 0 || d > 7)
            throw new RangeError(`bad dir ${d} at ${i}`);
        let len = 1;
        while (len < MAX_RUN_LEN && i + len < dirs.length && dirs[i + len] === d)
            len++;
        out += encodeRun(d, len);
        i += len;
    }
    return out;
}
/** Packs a tile path (start tile excluded) that begins next to (x0, y0). */
export function packPathFromTiles(x0, y0, tiles) {
    const dirs = [];
    let px = x0;
    let py = y0;
    for (const n of tiles) {
        dirs.push(dirFromDelta(n.x - px, n.y - py));
        px = n.x;
        py = n.y;
    }
    return packPath(dirs);
}
function decodeChar(p, i) {
    const c = p.charCodeAt(i);
    if (!(c >= PATH_CHAR_MIN && c <= PATH_CHAR_MAX)) {
        throw new RangeError(`bad PackedPath char code ${c} at ${i}`);
    }
    const v = c - PATH_CHAR_MIN;
    return { dir: Math.floor(v / MAX_RUN_LEN), len: (v % MAX_RUN_LEN) + 1 };
}
/** Expands a packed path to one Dir per tile. Throws on out-of-range chars. */
export function unpackPath(p) {
    const out = [];
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        for (let k = 0; k < len; k++)
            out.push(dir);
    }
    return out;
}
/** Number of tiles a packed path walks. Throws on out-of-range chars. */
export function pathTileCount(p) {
    let n = 0;
    for (let i = 0; i < p.length; i++)
        n += decodeChar(p, i).len;
    return n;
}
/** True when every char is in range and the path is within MAX_PATH_RUNS / MAX_PATH_TILES. */
export function isValidPackedPath(p) {
    if (p.length > MAX_PATH_RUNS)
        return false;
    let tiles = 0;
    for (let i = 0; i < p.length; i++) {
        const c = p.charCodeAt(i);
        if (!(c >= PATH_CHAR_MIN && c <= PATH_CHAR_MAX))
            return false;
        tiles += ((c - PATH_CHAR_MIN) % MAX_RUN_LEN) + 1;
    }
    return tiles <= MAX_PATH_TILES;
}
/** Tiles visited after (x0, y0), in order. Excludes the start tile. */
export function pathTiles(x0, y0, p) {
    const out = [];
    let x = x0;
    let y = y0;
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        for (let k = 0; k < len; k++) {
            x += DIR_DX[dir];
            y += DIR_DY[dir];
            out.push([x, y]);
        }
    }
    return out;
}
/** Final tile of a path from (x0, y0). */
export function pathEnd(x0, y0, p) {
    let x = x0;
    let y = y0;
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        x += DIR_DX[dir] * len;
        y += DIR_DY[dir] * len;
    }
    return [x, y];
}
/** Human-readable path for logs, e.g. "E×5 SE×3 S×7". */
export function debugPath(p) {
    const parts = [];
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        parts.push(`${DIR_NAMES[dir]}×${len}`);
    }
    return parts.join(' ');
}
// ---------------------------------------------------------------------------
// Timing. Base numbers are tuning defaults owned by Game Design (doc 10).
// ---------------------------------------------------------------------------
/** One straight tile at walk speed (2.5 tiles/s). */
export const STEP_MS_WALK = 400;
export const GAIT_MULT = {
    [Gait.Walk]: 1,
    [Gait.Run]: 0.55,
    [Gait.Carry]: 1.25,
};
/** Duration of one tile step in direction `d` at gait `g`. */
export function stepMs(d, g) {
    // Unknown gait values (e.g. a newer client) fall back to walk speed.
    return STEP_MS_WALK * (GAIT_MULT[g] ?? 1) * (isDiagonal(d) ? Math.SQRT2 : 1);
}
/** Total walking time of a packed path. */
export function pathDurationMs(p, g) {
    let total = 0;
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        total += stepMs(dir, g) * len;
    }
    return total;
}
/**
 * Position of a mover at room time `t`. Deterministic: the same inputs give
 * the same float result on server and client. This is the ONLY copy: the
 * engine's PathMover imports it from here (98 Q-18).
 *
 * - `t <= startAt` or empty path: resting at (x, y).
 * - during the path: linear within each tile step, continuous across runs.
 * - after the end: resting on the final tile.
 */
export function positionAt(m, t) {
    const p = m.path;
    const elapsed = t - m.startAt;
    if (p.length === 0 || !(elapsed > 0))
        return { x: m.x, y: m.y, dir: -1, moving: false };
    let cx = m.x;
    let cy = m.y;
    let acc = 0;
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        const s = stepMs(dir, m.gait);
        const runDur = s * len;
        if (elapsed < acc + runDur) {
            const k = (elapsed - acc) / s;
            return { x: cx + DIR_DX[dir] * k, y: cy + DIR_DY[dir] * k, dir, moving: true };
        }
        acc += runDur;
        cx += DIR_DX[dir] * len;
        cy += DIR_DY[dir] * len;
    }
    return { x: cx, y: cy, dir: -1, moving: false };
}
/**
 * Re-path rule: the tile the mover is committed to at time `t`, i.e. the next
 * tile boundary at or after `t`, and the room time it is reached.
 *
 * - `t <= startAt` or empty path: the start tile, `at = t` (it is there now).
 * - exactly on a boundary: that tile, `at = t`.
 * - mid-step: the tile being walked into, `at` = arrival time.
 * - after the end: the final tile, `at = t`.
 *
 * A new path for this mover starts at {x, y} with `startAt = at`.
 */
export function commitPoint(m, t) {
    const p = m.path;
    const elapsed = t - m.startAt;
    if (p.length === 0 || !(elapsed > 0))
        return { x: m.x, y: m.y, at: t };
    let cx = m.x;
    let cy = m.y;
    let acc = 0;
    for (let i = 0; i < p.length; i++) {
        const { dir, len } = decodeChar(p, i);
        const s = stepMs(dir, m.gait);
        const runDur = s * len;
        if (elapsed <= acc + runDur) {
            const j = Math.min(len, Math.ceil((elapsed - acc) / s));
            const at = m.startAt + acc + s * j;
            return { x: cx + DIR_DX[dir] * j, y: cy + DIR_DY[dir] * j, at: Math.max(at, t) };
        }
        acc += runDur;
        cx += DIR_DX[dir] * len;
        cy += DIR_DY[dir] * len;
    }
    return { x: cx, y: cy, at: t };
}
